import { useInfiniteQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getAgelessAssessments } from "api/methods";
import { AgelessFooter } from "atoms/ageless-footer/AgelessFooter";
import { AgelessPageShell } from "atoms/ageless-page-shell/AgelessPageShell";
import { EmptyState } from "molecules/empty-state/EmptyState";
import { HistoryListEntryProps } from "molecules/history-list-entry/HistoryListEntry";
import { HistoryList } from "organisms/history-list/HistoryList";
import { AccountDestinationSkeleton } from "./AccountDestinationSkeleton";

import { infiniteQueryKey } from "api/queryKeys";

const PAGE_SIZE = 25;
const MAX_DISPLAYED_TREATMENTS = 3;

const formatAssessmentTitle = (
  treatmentNames: string[] | undefined,
  isWellness: boolean,
  hasHarmonyScore: boolean,
  t: ReturnType<typeof useTranslation>["t"],
): string => {
  if (treatmentNames && treatmentNames.length > 0) {
    if (treatmentNames.length <= MAX_DISPLAYED_TREATMENTS) {
      return treatmentNames.join(", ");
    }
    const shown = treatmentNames.slice(0, MAX_DISPLAYED_TREATMENTS).join(", ");
    return t("assessmentHistory.moreTreatments", {
      shown,
      count: treatmentNames.length - MAX_DISPLAYED_TREATMENTS,
    });
  }
  if (isWellness && hasHarmonyScore) return t("assessmentHistory.aestheticWellness");
  if (isWellness) return t("assessmentHistory.wellness");
  return t("assessmentHistory.aesthetic");
};

export const AssessmentHistoryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    fetchNextPage,
    data,
    hasNextPage,
    isPending: isLoading,
    isError,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: infiniteQueryKey(getAgelessAssessments.key({ pageSize: PAGE_SIZE })),
    queryFn: ({ pageParam = 1 }) => getAgelessAssessments({ page: pageParam, pageSize: PAGE_SIZE }),
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
  });

  const allItems = data?.pages.flatMap((page) => page.items) ?? [];

  if (isLoading) return <AccountDestinationSkeleton title={t("assessmentHistory.header")} />;

  const renderContent = () => {
    if (isError || data == null) {
      return (
        <EmptyState
          title={t("common.somethingWentWrong")}
          body={t("assessmentHistory.errorBody")}
          buttonText={t("assessmentHistory.goBack")}
          onClick={() => navigate("/account")}
        />
      );
    }

    if (allItems.length === 0) {
      return (
        <EmptyState
          title={t("assessmentHistory.emptyTitle")}
          body={t("assessmentHistory.emptyBody")}
          buttonText={t("assessmentHistory.goBack")}
          onClick={() => navigate("/account")}
        />
      );
    }

    const grouped: Record<string, HistoryListEntryProps[]> = {};
    for (const item of allItems) {
      const date = dayjs(item.createdAt);
      const key = date.format("YYYY-MM");
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({
        id: item.id,
        title: formatAssessmentTitle(
          item.treatmentDisplayNames,
          item.isWellness,
          item.harmonyScore != null,
          t,
        ),
        subtitle:
          [
            item.isProviderConsult ? t("assessmentHistory.inOfficeConsultation") : null,
            item.isProviderEdited ? t("assessmentHistory.reviewedByProvider") : null,
            item.harmonyScore == null
              ? null
              : t("assessmentHistory.beautyScore", { score: Math.round(item.harmonyScore) }),
          ]
            .filter(Boolean)
            .join(" · ") || null,
        date: item.createdAt,
        onClick: () => navigate(`/account/assessments/${item.id}`),
      } satisfies HistoryListEntryProps);
    }

    const historyListItems = Object.entries(grouped)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([yearMonthKey, items]) => ({
        date: yearMonthKey,
        items: [...items].sort((a, b) => {
          const dateA = a.date ?? "";
          const dateB = b.date ?? "";
          return dateB.localeCompare(dateA);
        }),
      }));

    return (
      <div className="flex flex-col text-center">
        <HistoryList
          historyListItems={historyListItems}
          showLoadMoreResultsButton={hasNextPage}
          onLoadMoreClick={() => {
            fetchNextPage();
          }}
        />
      </div>
    );
  };

  return (
    <AgelessPageShell title={t("assessmentHistory.header")} onBack={() => navigate("/account")}>
      {renderContent()}
      <div className="grow" />
      <AgelessFooter />
    </AgelessPageShell>
  );
};
